/* ══════════════════════════════════════════════════════════════
   groww.ts — Groww API Client
   Portfolio view, stock & mutual fund holdings
   ══════════════════════════════════════════════════════════════ */

export interface GrowwConfig {
    accessToken: string;
    baseUrl: string;
}

export interface GrowwHolding {
    symbol: string;
    companyName: string;
    quantity: number;
    averagePrice: number;
    currentPrice: number;
    investedValue: number;
    currentValue: number;
    pnl: number;
    pnlPercent: number;
    dayChange: number;
}

export interface GrowwMFHolding {
    schemeName: string;
    folioNumber: string;
    units: number;
    nav: number;
    investedAmount: number;
    currentValue: number;
    returns: number;
    returnsPercent: number;
    xirr: number;
}

export interface GrowwProfile {
    userId: string;
    userName: string;
    email: string;
    broker: 'groww';
    panNumber: string;
}

const DEFAULT_BASE_URL = 'https://groww.in/v1/api';

function getConfig(): GrowwConfig {
    return {
        accessToken: localStorage.getItem('imm-groww-access-token') || '',
        baseUrl: DEFAULT_BASE_URL,
    };
}

async function growwRequest<T>(endpoint: string): Promise<T> {
    const config = getConfig();
    if (!config.accessToken) {
        throw new Error('Groww access token not set.');
    }

    const response = await fetch(`${config.baseUrl}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${config.accessToken}` },
    });

    if (!response.ok) {
        throw new Error(`Groww API error: ${response.status}`);
    }

    return response.json();
}

// ─── Portfolio ───

/** Get stock holdings */
export async function getStockHoldings(): Promise<GrowwHolding[]> {
    try {
        return await growwRequest<GrowwHolding[]>('/stocks/holdings');
    } catch {
        return [];
    }
}

/** Get mutual fund holdings */
export async function getMFHoldings(): Promise<GrowwMFHolding[]> {
    try {
        return await growwRequest<GrowwMFHolding[]>('/mutual-funds/holdings');
    } catch {
        return [];
    }
}

/** Get profile */
export async function getProfile(): Promise<GrowwProfile> {
    try {
        return await growwRequest<GrowwProfile>('/user/profile');
    } catch {
        return { userId: '', userName: 'Disconnected', email: '', broker: 'groww', panNumber: '' };
    }
}

// ─── Connection Status ───

export function isConnected(): boolean {
    return !!localStorage.getItem('imm-groww-access-token');
}

export function disconnect(): void {
    localStorage.removeItem('imm-groww-access-token');
}
